import { Box, Checkbox, Flex, Text } from "@mantine/core"
import { CheckIcon, FileTextIcon, XIcon } from "@phosphor-icons/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { DeliveryApi } from "../../apis"
import { IDeliveryNotePlus } from "../../models/DeliveryNote"
import { IGeneral } from "../../store/General"
import { currencyFormat } from "../../utils"
import { dateTimeSecFormat } from "../../utils/date"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import { DnStatusBadge } from "../active-deliveries/atoms"
import { DeliveryRowAction } from "../delivery/action-controls"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { CodeStack, FrameFooter, TableFrame, TablePager } from "../delivery/atoms"
import { ControlPanel } from "../odoo/control-panel"
import { usePermission } from "../permission"
import { ColumnType, Table } from "../table"
import { BulkActionBar } from "../ui"
import { BulkPodDialog, BulkPodMode } from "./bulk-pod-dialog"
import { useDistributingFilters } from "../supplier-delivery-distributing/distributing-filters"
import { DistributingFilterPanel } from "../supplier-delivery-distributing/distributing-filter-panel"

function Lines({ top, bottom, mono }: Readonly<{ top: React.ReactNode; bottom?: React.ReactNode; mono?: boolean }>) {
  return (
    <Box>
      <Text fz="var(--nw-fz-body)" ff={mono ? "monospace" : undefined}>
        {top || "-"}
      </Text>
      {bottom && (
        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" ff={mono ? "monospace" : undefined}>
          {bottom}
        </Text>
      )}
    </Box>
  )
}

function Amount({ value }: Readonly<{ value: number | null | undefined }>) {
  return (
    <Text ff="monospace" fz="var(--nw-fz-mono)" ta="right">
      {currencyFormat(value ?? 0)}
    </Text>
  )
}

/** Хүргэсэн tab (canvas Ord_DeliveryTabsB): archive of DELIVERED notes. */
export function SupplierDeliveredList({ ref }: { ref: any }) {
  const navigate = useNavigate()
  const { t } = useTranslation(["delivery", "common"])
  const deliveryNoteStatus = useSelector((state: { general: IGeneral }) => state.general.deliveryNoteStatus)
  const pullSheetStatus = useSelector((state: { general: IGeneral }) => state.general.pullSheetStatus)
  const ORD_DN_DETAIL = usePermission("ORD_DN_DETAIL")
  const [filterOpen, setFilterOpen] = useState(false)
  const { filters, setFilter, clearFilters, activeFilterChips, searchQuery } = useDistributingFilters()

  const reload = () => ref.current?.reload()
  const { permissionState } = useDeliveryActions()
  const [checked, setChecked] = useState<IDeliveryNotePlus[]>([])
  const [bulk, setBulk] = useState<BulkPodMode | null>(null)

  // The list is DELIVERED-only and its rows carry no proof status (podVerify/podReject apply to a
  // missing status), so every row is selectable; the dialog skips rows without a pending proof.
  const isSelectable = (row: IDeliveryNotePlus) => !!row.id
  const isChecked = (row: IDeliveryNotePlus) => checked.some((c) => c.id === row.id)
  const toggle = (row: IDeliveryNotePlus) =>
    setChecked((cur) => (cur.some((c) => c.id === row.id) ? cur.filter((c) => c.id !== row.id) : [...cur, row]))
  const togglePage = (pageRows: IDeliveryNotePlus[]) =>
    setChecked((cur) => {
      const selectable = pageRows.filter(isSelectable)
      const allOn = selectable.length > 0 && selectable.every((r) => cur.some((c) => c.id === r.id))
      if (allOn) return cur.filter((c) => !selectable.some((r) => r.id === c.id))
      return [...cur, ...selectable.filter((r) => !cur.some((c) => c.id === r.id))]
    })

  const columns: ColumnType<IDeliveryNotePlus>[] = [
    {
      title: "",
      width: 40,
      renderCheck: (data: any) => {
        const pageRows: IDeliveryNotePlus[] = (data?.rows ?? []).filter(isSelectable)
        const on = pageRows.filter(isChecked).length
        return (
          <Checkbox
            aria-label={t("manage.pending.selectAll")}
            checked={pageRows.length > 0 && on === pageRows.length}
            indeterminate={on > 0 && on < pageRows.length}
            disabled={pageRows.length === 0}
            onChange={() => togglePage(pageRows)}
          />
        )
      },
      render: (record) => (
        <Box onClick={(e) => e.stopPropagation()}>
          <Checkbox
            aria-label={t("manage.pending.selectOne", { code: record?.refCode })}
            checked={isChecked(record)}
            disabled={!isSelectable(record)}
            onChange={() => toggle(record)}
          />
        </Box>
      ),
    },
    {
      title: t("manage.col.action"),
      width: 64,
      render: (record) => (
        <Box onClick={(e) => e.stopPropagation()}>
          <DeliveryRowAction
            label={t("delivery.viewPdf")}
            icon={<FileTextIcon />}
            state={{ allowed: !!record?.pdf, reason: record?.pdf ? null : t("manage.reason.noReceipt") }}
            onClick={() => {
              if (record.pdf) window.open(record.pdf, "_blank", "noopener,noreferrer")
            }}
          />
        </Box>
      ),
    },
    {
      title: t("manage.delivered.colNumber"),
      width: 120,
      sorter: true,
      dataIndex: "refCode",
      render: (record) => <CodeStack primary={record?.refCode} secondary={record?.salesCode} />,
    },
    {
      title: t("delivery.deliveryStatus"),
      width: 132,
      sorter: true,
      dataIndex: "deliveryNoteStatus",
      render: (record) => <DnStatusBadge status={record?.deliveryNoteStatus} />,
    },
    {
      title: t("manage.delivered.colDates"),
      width: 168,
      sorter: true,
      dataIndex: "deliveryDate",
      render: (record) => (
        <Lines mono top={dateTimeSecFormat(record?.deliveryDate)} bottom={dateTimeSecFormat(record?.dispatchedDate)} />
      ),
    },
    {
      title: t("delivery.deliveryStaff"),
      sorter: true,
      dataIndex: "staffFirstNameEmail",
      render: (record) => <Lines top={renderName(record.staff)} bottom={record?.staff?.email} />,
    },
    {
      title: t("manage.delivered.colCustomer"),
      sorter: true,
      dataIndex: "receiverBusinessProfileNameRefCode",
      render: (record) => (
        <Lines
          top={renderBusinessName(record?.receiverBusiness, "-")}
          bottom={[
            record?.receiverBusiness?.refCode,
            [record?.receiverBranch?.refCode, record?.receiverBranch?.name].filter(Boolean).join(", "),
          ]
            .filter(Boolean)
            .join(" · ")}
        />
      ),
    },
    {
      title: t("delivery.paymentTerm"),
      width: 150,
      sorter: true,
      dataIndex: "paymentTermDescription",
      render: (record) => <Lines top={record?.paymentTerm?.description} />,
    },
    {
      title: t("delivery.totalAmount"),
      width: 104,
      sorter: true,
      dataIndex: "totalAmount",
      titleAlign: "right",
      render: (record) => <Amount value={record?.totalAmount} />,
    },
    {
      title: t("delivery.amountToPay"),
      width: 104,
      sorter: true,
      dataIndex: "amountToPay",
      titleAlign: "right",
      render: (record) => <Amount value={record?.amountToPay} />,
    },
    {
      title: t("delivery.paidAmount"),
      width: 96,
      sorter: true,
      dataIndex: "paidAmount",
      titleAlign: "right",
      render: (record) => <Amount value={record?.paidAmount} />,
    },
  ]

  return (
    <Flex direction="column" gap="sm" mih="60vh">
      <ControlPanel
        searchValue={searchQuery}
        onSearchChange={(v) => setFilter("searchQuery", v)}
        onSearch={() => reload()}
        filterChips={activeFilterChips}
        onFilterClick={() => setFilterOpen(!filterOpen)}
        filterOpen={filterOpen}
      />
      <DistributingFilterPanel
        opened={filterOpen}
        filters={filters}
        setFilter={setFilter}
        clearFilters={clearFilters}
        deliveryNoteStatus={deliveryNoteStatus}
        pullSheetStatus={pullSheetStatus}
        onSearch={reload}
      />
      <TableFrame>
        <Table
          ref={ref}
          name="supplier-delivery-distribute-confirm.list"
          filters={{ ...filters, deliveryNoteStatus: "DELIVERED" }}
          columns={columns}
          horizontalSpacing="sm"
          loadData={(data) => DeliveryApi.list(data!)}
          striped={false}
          limit={20}
          footer={(p) => (
            <FrameFooter>
              <TablePager page={p.page} pageSize={p.limit} total={p.total} onPageChange={p.setPage} onPageSizeChange={p.setLimit} />
            </FrameFooter>
          )}
          onRowClick={ORD_DN_DETAIL.hasAccess ? (record) => navigate(`/supplier-delivery/detail/${record.id}`) : undefined}
        />
      </TableFrame>
      <BulkActionBar
        selectedCount={checked.length}
        onClear={() => setChecked([])}
        actions={[
          {
            key: "reject",
            label: t("delivery.pod.reject"),
            icon: <XIcon size={16} />,
            reason: permissionState("podReject").reason,
            onClick: () => setBulk("reject"),
            helpKey: "order.supplier-delivery-list.action.pod-reject", reasonHelpKey: "order.supplier-delivery-list.reason.pod-reject", reasonCode: permissionState("podReject").allowed ? null : "PERM", helpId: "bulk-pod-reject",
          },
          {
            key: "verify",
            label: t("delivery.pod.verify"),
            icon: <CheckIcon size={16} />,
            primary: true,
            reason: permissionState("podVerify").reason,
            onClick: () => setBulk("verify"),
            helpKey: "order.supplier-delivery-list.action.pod-verify", reasonHelpKey: "order.supplier-delivery-list.reason.pod-verify", reasonCode: permissionState("podVerify").allowed ? null : "PERM", helpId: "bulk-pod-verify",
          },
        ]}
      />
      {bulk && (
        <BulkPodDialog
          mode={bulk}
          rows={checked}
          onClose={() => setBulk(null)}
          onDone={() => {
            setBulk(null)
            setChecked([])
            reload()
          }}
        />
      )}
    </Flex>
  )
}
